import { useState } from "react";
import { Link } from "react-router-dom";

const defaultFaqs = [
  {
    question: "What platforms do you specialise in?",
    answer:
      "We specialise across three core platforms — Shopify, TikTok Shop, and eBay UK. Whether you need a full store build, product listing optimisation, or end-to-end account management, we cover the entire ecosystem from setup to scale.",
  },
  {
    question: "How long does it take to launch a Shopify store?",
    answer:
      "A standard Shopify store build typically takes 2–4 weeks depending on complexity, number of products, and custom functionality required. We follow a structured Discovery & Strategy phase first to ensure everything is scoped correctly before development begins.",
  },
  {
    question: "Do you manage TikTok Shop accounts on an ongoing basis?",
    answer:
      "Yes. We offer monthly retainer packages that cover product listing management, content strategy, affiliate outreach, and performance reporting. Our team handles the day-to-day so you can focus on sourcing and fulfilment.",
  },
  {
    question: "What does your eBay UK service include?",
    answer:
      "Our eBay UK service covers store setup and branding, SEO-optimised product listings, pricing strategy, competitor analysis, and ongoing account health monitoring. We also handle any policy compliance issues and help scale your catalogue efficiently.",
  },
  {
    question: "Can you help with paid advertising on these platforms?",
    answer:
      "Absolutely. We run PPC campaigns across Shopify (Google & Meta), TikTok Ads, and eBay Promoted Listings. Every campaign is built around your margin targets with clear monthly reporting so you always know your return on ad spend.",
  },
  {
    question: "Do you offer SEO and content writing services?",
    answer:
      "Yes — SEO and content writing are part of our core service suite. From product description copywriting and blog content to technical on-page SEO, we cover everything needed to drive organic traffic to your store.",
  },
  {
    question: "How are your services priced?",
    answer:
      "Our services are available as monthly subscriptions, making it easy to scale up or down as your business grows. Pricing varies by service and scope — visit our pricing page to explore plans or get in touch for a custom quote.",
  },
  {
    question: "What results can I realistically expect?",
    answer:
      "Results depend on your starting point, budget, and platform — but our clients typically see meaningful improvements in store traffic, conversion rate, and revenue within the first 60–90 days. We set clear KPIs at the start of every engagement so expectations are aligned from day one.",
  },
];

export default function FAQSection({ items, heading, subtitle }) {
  const [openIndex, setOpenIndex] = useState(null);

  // Fallback to static items if no dynamic track array is passed down
  const currentFaqs = items || defaultFaqs;

  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  if (!currentFaqs || currentFaqs.length === 0) return null;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600&family=Cormorant+Garamond:wght@400;500;600&display=swap');

        .faq-section {
          width: 100%;
          background-color: #000000;
          padding: 100px 0 120px;
          font-family: 'DM Sans', sans-serif;
          background-size: cover;
          background-position: center;
        }

        .faq-container {
          max-width: 860px;
          margin: 0 auto;
          padding: 0 24px;
        }

        /* ── Header ── */
        .faq-header {
          margin-bottom: 64px;
          display: flex;
          flex-direction: column;
          gap: 14px;
        }
        .faq-eyebrow {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: #F97316;
        }
        .faq-eyebrow::before {
          content: '';
          display: inline-block;
          width: 22px;
          height: 1.5px;
          background: #F97316;
        }
        .faq-title {
          font-family: 'epilogue', serif;
          font-size: clamp(2.4rem, 4vw, 3.4rem);
          font-weight: 600;
          color: #fffefc;
          line-height: 1.1;
          letter-spacing: -0.02em;
        }
        .faq-subtitle {
          font-size: 15px;
          color: #ab977d;
          line-height: 1.65;
          max-width: 520px;
           font-family: 'epilogue', serif;
        }

        /* ── Accordion list ── */
        .faq-list {
          display: flex;
          flex-direction: column;
          border-top: 1px solid #EDE4D8;
           font-family: 'epilogue', serif;
        }

        /* ── Single item ── */
        .faq-item {
          border-bottom: 1px solid #EDE4D8;
          overflow: hidden;
           font-family: 'epilogue', serif;
        }

        .faq-trigger {
          width: 100%;
          background: none;
          border: none;
          cursor: pointer;
          padding: 28px 0;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 24px;
          text-align: left;
        }

        .faq-question {
          font-size: 16px;
          font-weight: 500;
          color: #dcdad8;
          line-height: 1.4;
          transition: color 0.25s;
          font-family: 'epilogue', serif;
        }
        .faq-item.open .faq-question {
          color: #F97316;
        }

        /* Icon */
        .faq-icon {
          flex-shrink: 0;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 1px solid #EDE4D8;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.25s, border-color 0.25s, transform 0.35s ease;
          background: transparent;
        }
        .faq-item.open .faq-icon {
          background: #F97316;
          border-color: #F97316;
          transform: rotate(45deg);
        }
        .faq-icon svg {
          width: 14px;
          height: 14px;
          stroke: #9A8A77;
          transition: stroke 0.25s;
        }
        .faq-item.open .faq-icon svg {
          stroke: #FFFFFF;
        }

        /* Answer panel */
        .faq-body {
          display: grid;
          grid-template-rows: 0fr;
          transition: grid-template-rows 0.38s ease;
           font-family: 'epilogue', serif;
        }
        .faq-item.open .faq-body {
          grid-template-rows: 1fr;
        }
        .faq-body-inner {
          overflow: hidden;
        }
        .faq-answer {
          padding: 0 56px 28px 0;
          font-size: 14px;
          color: #ab977d;
          line-height: 1.75;
          max-width: 720px;
           font-family: 'epilogue', serif;
        }

        /* ── Bottom CTA strip ── */
        .faq-cta-strip {
          margin-top: 64px;
          padding: 36px 44px;
          background: #F97316;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 24px;
        }
        .faq-cta-strip p {
          font-size: 14px;
          color: #FFFFFF;
          line-height: 1.55;
          max-width: 420px;
          margin: 0;
           font-family: 'epilogue', serif;
        }
        .faq-cta-strip p strong {
          display: block;
          font-family: 'epilogue', serif;
          font-size: 1.5rem;
          font-weight: 500;
          color: #FFFFFF;
          margin-bottom: 6px;
        }
        .faq-cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          background: #000000;
          color: #FFFFFF;
          font-family: 'epilogue', serif;
          font-size: 13.5px;
          font-weight: 500;
          border-radius: 8px;
          text-decoration: none;
          white-space: nowrap;
          border: none;
          cursor: pointer;
          transition: background 0.2s, transform 0.2s;
          flex-shrink: 0;
        }
        .faq-cta-btn:hover {
          background: #1A1208;
          transform: translateY(-2px);
        }

        @media (max-width: 640px) {
          .faq-section { padding: 72px 0 88px; }
          .faq-answer { padding-right: 0; }
          .faq-cta-strip {
            flex-direction: column;
            align-items: flex-start;
            padding: 28px 28px;
          }
        }
      `}</style>

      <section className="faq-section">
        <div className="faq-container">

          {/* Header */}
          <div className="faq-header">
            <span className="faq-eyebrow">Got Questions</span>
            <h2 className="faq-title">{heading || <>Frequently<br />Asked Questions</>}</h2>
            <p className="faq-subtitle">
              {subtitle || "Everything you need to know about our services, platforms, and process. Can't find an answer? Drop us a message."}
            </p>
          </div>

          {/* Accordion */}
          <div className="faq-list">
            {currentFaqs.map((faq, i) => {
              const isOpen = openIndex === i;
              return (
                <div key={i} className={`faq-item${isOpen ? " open" : ""}`}>
                  <button
                    className="faq-trigger"
                    onClick={() => toggle(i)}
                    aria-expanded={isOpen}
                  >
                    <span className="faq-question">{faq.question}</span>
                    <span className="faq-icon" aria-hidden="true">
                      <svg viewBox="0 0 14 14" fill="none" strokeWidth="1.8" strokeLinecap="round">
                        <line x1="7" y1="1" x2="7" y2="13" />
                        <line x1="1" y1="7" x2="13" y2="7" />
                      </svg>
                    </span>
                  </button>
                  <div className="faq-body" role="region">
                    <div className="faq-body-inner">
                      <p className="faq-answer">{faq.answer}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom CTA */}
          <div className="faq-cta-strip">
            <p>
              <strong>Still have questions?</strong>
              Our team is happy to walk you through anything — services, pricing, or platform strategy.
            </p>
            <a href="/contact" className="faq-cta-btn">
              Get in Touch &rarr;
            </a>
          </div>

        </div>
      </section>
    </>
  );
}